import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { useTier } from '../context/TierContext';
import { RepCell, MetricBar, ScoreCell, LoadingState, ErrorState, EmptyState } from '../components/primitives';
import RangeControl, { DEFAULT_RANGE, rangeLabel } from '../components/RangeControl';
import RepSeriesPanel from '../components/RepSeriesPanel';
import { fmtInt, initialsOf } from '../lib/format';
import { RangeRep, TimeRange } from '../types/superadmin';

type SortKey =
  | 'callQualityScore'
  | 'trainingQualityScore'
  | 'trainingCompletion'
  | 'sessions'
  | 'minutes'
  | 'objFaqScore'
  | 'landmineScore'
  | 'competitorScore'
  | 'playbookScore';

const COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: 'sessions', label: 'Sessions' },
  { key: 'minutes', label: 'Minutes' },
  { key: 'objFaqScore', label: 'Obj/FAQ' },
  { key: 'landmineScore', label: 'Landmine' },
  { key: 'competitorScore', label: 'Competitor' },
  { key: 'playbookScore', label: 'Playbook' },
  { key: 'callQualityScore', label: 'Call quality' },
  { key: 'trainingQualityScore', label: 'Training quality' },
  { key: 'trainingCompletion', label: 'Training completion' }
];

// #, Rep, Client + score columns — for the expanded-row colSpan.
const TOTAL_COLS = 3 + COLUMNS.length;

const repKey = (r: RangeRep) => `${r.tenantId}|${r.userId}`;

export default function Reps() {
  const [range, setRange] = useState<TimeRange>(DEFAULT_RANGE);
  const { tier } = useTier();
  const { data, loading, error } = useApiData(() => api.getReps(range, tier), [JSON.stringify(range), tier]);
  const [sortKey, setSortKey] = useState<SortKey>('callQualityScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState<string | null>(null);

  const reps = useMemo(() => data?.data.reps ?? [], [data]);

  const sorted = useMemo(() => {
    return [...reps].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls always last
      if (bv == null) return -1;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [reps, sortKey, sortDir]);

  const setSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else {
      setSortKey(k);
      setSortDir('desc');
    }
  };
  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === 'desc' ? '↓' : '↑') : '↕');
  const thCls = (k: SortKey) => 'sortable' + (sortKey === k ? ' sorted' : '');
  const toggle = (k: string) => setExpanded((cur) => (cur === k ? null : k));

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Reps · {rangeLabel(range)}</div>
          <h1 className="title">
            <span className="serif">Call quality and training quality, side by side</span>
          </h1>
          <p className="subtitle">
            Every rep's <b>call quality score</b> (live-call handling + playbook), <b>training quality score</b>{' '}
            and <b>training completion</b> (abandoned sessions count as 0%) for the selected period. Click a rep to
            see their activity by day.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <RangeControl value={range} onChange={setRange} />
        </div>
      </div>

      {loading && <LoadingState />}
      {error && <ErrorState message={error} />}

      {!loading && !error && (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Rep</th>
                <th>Client</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} className={thCls(c.key)} onClick={() => setSort(c.key)}>
                    {c.label} <span className="sort-arr">{arrow(c.key)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r: RangeRep, i) => {
                const k = repKey(r);
                const isOpen = expanded === k;
                return (
                  <React.Fragment key={k}>
                    <tr className="clickable" onClick={() => toggle(k)}>
                      <td>
                        <span className="mono" style={{ color: 'var(--ink-muted)' }}>
                          <span className={'caret' + (isOpen ? ' open' : '')}>▸</span>
                          {String(i + 1).padStart(2, '0')}
                        </span>
                      </td>
                      <td>
                        <RepCell initials={initialsOf(r.name)} name={r.name} role={r.email || undefined} />
                      </td>
                      <td>
                        <Link
                          to={`/clients/${r.tenantId}`}
                          className="mono"
                          style={{ color: 'var(--ink-muted)', textDecoration: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.displayName}
                        </Link>
                      </td>
                      <td>
                        <span className="metric-num">{fmtInt(r.sessions)}</span>
                      </td>
                      <td>
                        <span className="metric-num">
                          {fmtInt(r.minutes)}
                          <span className="u">min</span>
                        </span>
                      </td>
                      <td>
                        <ScoreCell value={r.objFaqScore} />
                      </td>
                      <td>
                        <ScoreCell value={r.landmineScore} />
                      </td>
                      <td>
                        <ScoreCell value={r.competitorScore} />
                      </td>
                      <td>
                        <ScoreCell value={r.playbookScore} />
                      </td>
                      <td>
                        <MetricBar value={r.callQualityScore} />
                      </td>
                      <td>
                        {r.trainingSessions > 0 ? <MetricBar value={r.trainingQualityScore} /> : <span className="caption">—</span>}
                      </td>
                      <td>
                        {r.trainingSessions > 0 ? <MetricBar value={r.trainingCompletion} /> : <span className="caption">—</span>}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="rep-detail-row">
                        <td colSpan={TOTAL_COLS}>
                          <RepSeriesPanel rep={r} range={range} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={TOTAL_COLS}>
                    <EmptyState>No rep activity in this range. Try a wider period.</EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
